(() => {
  const SECTION_SELECTOR = '[data-kalaboost-ingredient]';
  const SCROLLER_SELECTOR = '[data-kalaboost-ingredient-scroller]';
  const CARD_SELECTOR = '[data-kalaboost-ingredient-card]';
  const READY_ATTR = 'data-kalaboost-ingredient-ready';
  const MOBILE_MQ = '(max-width: 1199px)';
  const RESUME_DELAY_MS = 2500;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const parseDelayMs = (section) => {
    const seconds = Number.parseFloat(section.dataset.mobileAutoplayDelay || '4');
    if (!Number.isFinite(seconds) || seconds <= 0) return 4000;
    return Math.round(seconds * 1000);
  };

  const getCards = (scroller) => Array.from(scroller.querySelectorAll(CARD_SELECTOR));

  const canAutoplay = (section, scroller) => {
    if (section.dataset.mobileAutoplay !== 'true') return false;
    if (!window.matchMedia(MOBILE_MQ).matches) return false;
    if (prefersReducedMotion()) return false;

    const cards = getCards(scroller);
    if (cards.length < 2) return false;

    return scroller.scrollWidth > scroller.clientWidth + 4;
  };

  const getCardScrollLeft = (scroller, card) => {
    const scrollerRect = scroller.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    return scroller.scrollLeft + (cardRect.left - scrollerRect.left);
  };

  const getNearestIndex = (scroller, cards) => {
    const scrollLeft = scroller.scrollLeft;
    let nearestIndex = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    cards.forEach((card, index) => {
      const distance = Math.abs(getCardScrollLeft(scroller, card) - scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });

    return nearestIndex;
  };

  const scrollToCard = (scroller, card, behavior = 'smooth') => {
    if (!card) return;

    scroller.scrollTo({
      left: getCardScrollLeft(scroller, card),
      behavior,
    });
  };

  const initSection = (section) => {
    if (!(section instanceof Element)) return;
    if (section.getAttribute(READY_ATTR) === 'true') return;

    const scroller = section.querySelector(SCROLLER_SELECTOR);
    if (!scroller) return;

    let timerId = null;
    let resumeTimerId = null;
    let pausedByUser = false;
    let isProgrammaticScroll = false;

    const clearTimer = () => {
      if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
    };

    const clearResumeTimer = () => {
      if (resumeTimerId !== null) {
        window.clearTimeout(resumeTimerId);
        resumeTimerId = null;
      }
    };

    const advance = () => {
      if (pausedByUser || !canAutoplay(section, scroller)) return;

      const cards = getCards(scroller);
      if (cards.length < 2) return;

      const currentIndex = getNearestIndex(scroller, cards);
      const nextIndex = currentIndex >= cards.length - 1 ? 0 : currentIndex + 1;
      const behavior = nextIndex === 0 ? 'auto' : 'smooth';

      isProgrammaticScroll = true;
      scrollToCard(scroller, cards[nextIndex], behavior);

      window.setTimeout(() => {
        isProgrammaticScroll = false;
      }, 450);
    };

    const start = () => {
      clearTimer();
      if (!canAutoplay(section, scroller) || pausedByUser) return;
      timerId = window.setInterval(advance, parseDelayMs(section));
    };

    const pauseForUser = () => {
      pausedByUser = true;
      clearTimer();
      clearResumeTimer();

      resumeTimerId = window.setTimeout(() => {
        pausedByUser = false;
        start();
      }, RESUME_DELAY_MS);
    };

    const onScroll = () => {
      if (isProgrammaticScroll) return;
      pauseForUser();
    };

    const onPointerDown = () => {
      pauseForUser();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        clearTimer();
        return;
      }

      if (!pausedByUser) start();
    };

    const onResize = () => {
      clearTimer();
      if (!pausedByUser) start();
    };

    scroller.addEventListener('scroll', onScroll, { passive: true });
    scroller.addEventListener('pointerdown', onPointerDown, { passive: true });
    scroller.addEventListener('touchstart', onPointerDown, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('resize', onResize);

    start();
    section.setAttribute(READY_ATTR, 'true');
  };

  const initAll = (root = document) => {
    root.querySelectorAll(SECTION_SELECTOR).forEach(initSection);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    initAll(event.target);
  });
})();
